import { useCallback, useRef } from "react";
import { useRouter } from "solito/router";
import type { z } from "zod";

import { Flag } from "@medbillai/utils/flag";
import {
  caseIntakeFlowErrorUrl,
  caseIntakeFlowUrl,
} from "@medbillai/utils/urls";

import type { ListSelectSchema } from "../../../screens/flows/common/forms/ListSelectForm";
import { newPerson } from "../../../screens/person/utils/utils";
import { analyticsService } from "../../analytics";
import { useBoolVariation } from "../../flag";
import type { FieldData, FlowStepAction } from "../common";
import {
  lookupFlowStep,
  useBaseGotoFlowExit,
  useBaseGotoFlowInitialStep,
} from "../flowControl";
import { useCaseIntakeFlowStore } from "./caseIntakeStore";
import {
  type CaseIntakeFlowConfig,
  type CaseType,
  type FlowData,
  initialFlowConfig,
  initialFlowConfigPhase1,
} from "./constants";
import { useSubmit } from "./useSubmit";

export type CaseIntakeCallbackDecisions =
  | {
      step:
        | "case-type"
        | "bill-issue"
        | "benefits-question"
        | "upload-documents";
      input: z.infer<typeof ListSelectSchema>;
    }
  | {
      step: "person-select";
      input: {
        input: string | undefined;
      };
    };

const decisionMap: Record<
  CaseIntakeCallbackDecisions["step"],
  (flowData: FlowData) => string
> = {
  "case-type": (flowData: FlowData) => {
    if ("caseType" in flowData) {
      switch (flowData["caseType"]) {
        case "BILL_ISSUE":
          return "bill-issue";

        case "BENEFITS_QUESTION":
          return "benefits-question";

        default:
          return "upload-documents";
      }
    } else {
      throw new Error("caseType not found in flow data");
    }
  },
  // TODO remove this until needed
  "bill-issue": (flowData: FlowData) => {
    if ("billIssue" in flowData) {
      return "upload-documents";
    } else {
      throw new Error(
        `billIssue not found in flow data: ${JSON.stringify(flowData)}`,
      );
    }
  },
  // TODO remove this until needed
  "benefits-question": (flowData: FlowData) => {
    if ("benefitsQuestion" in flowData) {
      return "upload-documents";
    } else {
      throw new Error(
        `billIssue not found in flow data: ${JSON.stringify(flowData)}`,
      );
    }
  },
  "person-select": (flowData: FlowData) => {
    if ("personId" in flowData) {
      if (flowData["personId"] === newPerson.id) {
        return "person-name";
      }
      return "additional-details";
    } else {
      throw new Error("personId not found in flow data");
    }
  },
  "upload-documents": (flowData: FlowData) => {
    if ("caseType" in flowData) {
      switch (flowData["caseType"]) {
        case "APP_ISSUE":
          return "additional-details";

        default:
          return "person-select";
      }
    } else {
      throw new Error("caseType not found in flow data");
    }
  },
} as const;

const mapFieldsToFlowData = (
  flowConfig: CaseIntakeFlowConfig,
  caseType?: string,
) => {
  const flowData: Record<string, FieldData> = Object.values(flowConfig.steps)
    .flatMap(step => {
      if (
        step.type === "CARD_MULTI_SELECT" ||
        step.type === "CARD_SELECT" ||
        step.type === "LIST_SELECT" ||
        step.type === "PERSON_SELECT" ||
        step.type === "PERSON_DOB" ||
        step.type === "ADDITIONAL_DETAILS"
      ) {
        return [
          {
            [step.field.name]: undefined,
          },
        ];
      }

      if (step.type === "PERSON_NAME" || step.type === "UPLOAD_DOCUMENTS") {
        return [
          step.fields.map(field => {
            return {
              [field.name]: undefined,
            };
          }),
        ];
      }

      return [];
    })
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});

  if (caseType && "caseType" in flowData) {
    flowData["caseType"] = caseType;
  }

  return flowData;
};

export const useSetupCaseFlow = () => {
  const isInitializing = useRef(false);

  const { replace, push } = useRouter();

  const setFlowConfig = useCaseIntakeFlowStore(state => state.setFlowConfig);
  const setFlowData = useCaseIntakeFlowStore(state => state.setFlowData);
  const setCurrentStep = useCaseIntakeFlowStore(state => state.setCurrentStep);
  const setIsInitialized = useCaseIntakeFlowStore(
    state => state.setIsInitialized,
  );

  const CASE_INTAKE_V1_PHASE_2 = useBoolVariation(
    Flag.CASE_INTAKE_V1_PHASE_2,
    false,
  );

  const handleSetupAndPush = useCallback(
    ({ from, caseType }: { from: string; caseType?: CaseType }) => {
      if (isInitializing.current) {
        return;
      }

      isInitializing.current = true;

      let flowConfig: CaseIntakeFlowConfig;

      if (CASE_INTAKE_V1_PHASE_2) {
        flowConfig = initialFlowConfig();
      } else {
        flowConfig = initialFlowConfigPhase1();
      }

      const flowData = mapFieldsToFlowData(flowConfig, caseType);
      flowConfig.config.exit = from;

      if (flowData.caseType) {
        try {
          const decision = decisionMap["case-type"](flowData);

          flowConfig.config.initial = decision;
        } catch (error) {
          console.error("error decision for case type", flowData);
          replace(caseIntakeFlowErrorUrl());
        }
      }

      setFlowConfig(flowConfig);
      setFlowData(flowData);
      setCurrentStep(flowConfig.config.initial);

      setIsInitialized(true);
      isInitializing.current = false;

      push(caseIntakeFlowUrl(flowConfig.config.initial));
    },
    [
      CASE_INTAKE_V1_PHASE_2,
      push,
      replace,
      setCurrentStep,
      setFlowConfig,
      setFlowData,
      setIsInitialized,
    ],
  );

  return handleSetupAndPush;
};

export const useCanGoBackInFlow = () => {
  const flowConfig = useCaseIntakeFlowStore(state => state.flowConfig);
  const ls = useLookupFlowStep();

  const canGoBackInFlow = useCallback(
    (step: string) => {
      if (!flowConfig) {
        return false;
      }
      if (!step || flowConfig?.config.initial === step) {
        return false;
      } else {
        const stepConfig = ls(step);

        return stepConfig.options.canGoBack !== false;
      }
    },
    [flowConfig, ls],
  );

  return canGoBackInFlow;
};

export const useGotoFlowInitialStep = () => {
  const isInitialized = useCaseIntakeFlowStore(state => state.isInitialized);
  const flowConfig = useCaseIntakeFlowStore(state => state.flowConfig);

  const baseGotoFlowInitialStep = useBaseGotoFlowInitialStep({
    isInitialized,
    flowConfig,
    errorUrl: "/error",
  });

  const gotoFlowInitialStep = useCallback(
    () => baseGotoFlowInitialStep(),
    [baseGotoFlowInitialStep],
  );

  return gotoFlowInitialStep;
};

export const useGotoFlowExit = () => {
  const reset = useCaseIntakeFlowStore(state => state.reset);

  const isInitialized = useCaseIntakeFlowStore(state => state.isInitialized);
  const flowConfig = useCaseIntakeFlowStore(state => state.flowConfig);
  const currentStep = useCaseIntakeFlowStore(state => state.currentStep);

  const baseGotoFlowExit = useBaseGotoFlowExit({
    isInitialized,
    flowConfig,
    reset,
    errorUrl: "/error",
    analyticsTrigger: () => {
      analyticsService.trackEvent("CaseIntakeFlowExit", {
        from: currentStep,
      });
    },
  });

  const gotoFlowExit = useCallback(
    () => baseGotoFlowExit(),
    [baseGotoFlowExit],
  );

  return gotoFlowExit;
};

export const useLookupFlowStep = () => {
  const flowConfig = useCaseIntakeFlowStore(state => state.flowConfig);

  return (step: keyof CaseIntakeFlowConfig["steps"]) =>
    lookupFlowStep(flowConfig, step);
};

type DecisionMapKey = keyof typeof decisionMap;

function isDecisionMapKey(key: string): key is DecisionMapKey {
  return key in decisionMap;
}

export const useCaseIntakeFlowHandleOn = () => {
  const { push, replace } = useRouter();

  const isInitialized = useCaseIntakeFlowStore(state => state.isInitialized);
  const currentStep = useCaseIntakeFlowStore(state => state.currentStep);
  const flowConfig = useCaseIntakeFlowStore(state => state.flowConfig);
  const flowData = useCaseIntakeFlowStore(state => state.flowData);

  const setCurrentStep = useCaseIntakeFlowStore(state => state.setCurrentStep);
  const resetFlow = useCaseIntakeFlowStore(state => state.reset);

  // const { caseIntakeCallbackMap } = useCaseIntakeFlowCallbacks();
  const ls = useLookupFlowStep();
  const { submit } = useSubmit();

  const handleOn = useCallback(
    async (action: FlowStepAction, latestFlowData?: FlowData) => {
      // Use a snapshot of the current step to avoid race conditions which occur
      // on web for some reason
      const currentFlowStep = useCaseIntakeFlowStore.getState().currentStep;
      try {
        if (!isInitialized) {
          throw new Error("onboarding flow not initialized");
        }

        if (!flowConfig) {
          throw new Error("flow config not found");
        }

        if (!currentFlowStep) {
          throw new Error("current step not set");
        }

        const currentStepConfig = ls(currentFlowStep);

        if (!currentStepConfig) {
          throw new Error(`current step config not found ${currentFlowStep}`);
        }

        if (!currentStepConfig.on[action]) {
          throw new Error(`${action} step not found`);
        }

        if (currentStepConfig.on[action].goto === "exit") {
          analyticsService.trackEvent("CaseIntakeFlowExit", {
            from: currentStep,
          });

          resetFlow();
          replace(flowConfig.config.exit);
        } else if (currentStepConfig.on[action].goto === "submit") {
          if (latestFlowData) {
            await submit(latestFlowData);
          } else if (flowData) {
            await submit(flowData);
          } else {
            throw new Error("latestFlowData or flowData is undefined");
          }
        } else if (currentStepConfig.on[action].goto === "decision") {
          if (
            isDecisionMapKey(currentFlowStep) &&
            latestFlowData !== undefined
          ) {
            const decision = decisionMap[currentFlowStep](latestFlowData);

            if (decision === "exit") {
              analyticsService.trackEvent("CaseIntakeFlowExit", {
                from: currentStep,
              });

              resetFlow();
              replace(flowConfig.config.exit);
            } else if (decision === "submit") {
              await submit(latestFlowData);
            } else {
              analyticsService.trackEvent("CaseIntakeFlowStepForward", {
                from: currentFlowStep,
                to: decision,
              });

              setCurrentStep(decision);
              push(`${flowConfig.config.basePath}/${decision}`);
            }
          } else {
            throw new Error(
              `cannot make decision: map not found for step ${currentFlowStep}`,
            );
          }
        } else {
          if (ls(currentStepConfig.on[action].goto) === undefined) {
            throw new Error("next step not found");
          }

          analyticsService.trackEvent("CaseIntakeFlowStepForward", {
            from: currentFlowStep,
            to: currentStepConfig.on[action].goto,
          });

          setCurrentStep(currentStepConfig.on[action].goto);

          push(
            `${flowConfig.config.basePath}/${currentStepConfig.on[action].goto}`,
          );
        }
      } catch (error: unknown) {
        if (error instanceof Error) {
          console.error("error handling on action", error.message, action);
        } else {
          console.error("unknown error handling on action", error, action);
        }
        replace(caseIntakeFlowErrorUrl());
      }
    },
    [
      isInitialized,
      flowConfig,
      ls,
      currentStep,
      resetFlow,
      replace,
      flowData,
      submit,
      setCurrentStep,
      push,
    ],
  );

  const handleOnNext = useCallback(
    async (latestFlowData?: FlowData) => {
      try {
        await handleOn("next", latestFlowData);
      } catch (error) {
        console.error("error handling on next", error);
        replace(caseIntakeFlowErrorUrl());
      }
    },
    [handleOn, replace],
  );

  const handleOnSkip = useCallback(async () => {
    try {
      await handleOn("skip");
    } catch (error) {
      console.error("error handling on skip", error);
      replace(caseIntakeFlowErrorUrl());
    }
  }, [handleOn, replace]);

  return { handleOnNext, handleOnSkip };
};
